import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { GuideService } from './guide.service';

@Component({
  selector: 'app-guide-overlay',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule],
  templateUrl: './guide-overlay.component.html',
  styleUrl: './guide-overlay.component.css',
  host: {
    popover: 'manual',
    '(window:resize)': 'guide.refresh()',
    '(window:keydown.escape)': 'onEscape($event)',
  },
})
export class GuideOverlayComponent {
  readonly guide = inject(GuideService);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly dialog = inject(MatDialog);

  constructor() {
    const refresh = () => this.guide.refresh();
    document.addEventListener('scroll', refresh, true);
    inject(DestroyRef).onDestroy(() => document.removeEventListener('scroll', refresh, true));

    // Dialogs use the popover top layer, which paints above any z-index.
    // Showing this overlay again puts the tip back on top once the dialog is open.
    const opened = this.dialog.afterOpened.subscribe(() => {
      if (!this.guide.active()) {
        return;
      }
      setTimeout(() => {
        this.raise();
        this.guide.refresh();
      });
    });
    inject(DestroyRef).onDestroy(() => opened.unsubscribe());

    effect(() => {
      if (this.guide.active()) {
        this.raise();
      } else {
        this.hide();
      }
    });

    afterNextRender(() => {
      if (this.guide.active()) {
        this.raise();
      }
    });
  }

  /** Move this overlay to the top of the browser top layer. */
  private raise(): void {
    const el = this.host.nativeElement;
    if (typeof el.showPopover !== 'function') {
      return;
    }
    try {
      if (el.matches(':popover-open')) {
        el.hidePopover();
      }
      el.showPopover();
    } catch {
      // The host is not connected yet.
    }
  }

  private hide(): void {
    const el = this.host.nativeElement;
    if (typeof el.hidePopover !== 'function' || !el.matches(':popover-open')) {
      return;
    }
    el.hidePopover();
  }

  onEscape(event: Event): void {
    if (!this.guide.active()) {
      return;
    }
    event.preventDefault();
    this.guide.skip();
  }
}
